import { useQueryClient } from '@tanstack/react-query';
import { TRAVELLER_DOCUMENT_MAX_BYTES } from '@utazzo/shared';
import { cn, UzBadge, UzButton, UzCard, UzInput, UzSelect, UzSpinner } from '@utazzo/ui';
import {
  ArrowLeft,
  BadgeCheck,
  BellRing,
  CheckCircle2,
  ChevronDown,
  Download,
  ExternalLink,
  Eye,
  FileCheck2,
  FilePlus2,
  Mail,
  Map,
  Phone,
  RotateCcw,
  Upload,
  Users,
} from 'lucide-react';
import { useRef, useState } from 'react';
import { FaWhatsapp } from 'react-icons/fa';
import { useNavigate, useParams } from 'react-router-dom';

import { DepartmentChip } from '../components/DepartmentChip';
import { CaseDocuments } from '../components/OpsBookingsDrawer';
import { BookingJumpNav, KIND_ORDER, OpsBookingSections } from '../components/OpsBookingSections';
import { fileToBase64 } from '../file-base64';
import { actionableKindOf, DEPARTMENT_META, KIND_META } from '../item-kind';
import { payByTone } from '../pay-by';
import {
  opsCasesQK,
  useCompleteOpsCase,
  useOpenOpsCase,
  useOpsBookingSheet,
  useReopenOpsCase,
} from '../use-ops-cases';

import type { ContractStatus, OpsBookingSheet, TravellerDocumentType } from '@utazzo/shared';

import { PageShell } from '@/components/page-shell';
import { useCurrentUser } from '@/features/auth/use-auth';
import { useResendContract } from '@/features/contracts/use-contracts';
import {
  useCreateDocumentRequest,
  useDownloadDocumentFile,
  useOpenDocumentFile,
  useResendDocumentRequest,
  useUploadDocumentFile,
  useVerifyDocumentRequest,
} from '@/features/traveller-documents/use-traveller-documents';
import { openWhatsAppConversation } from '@/features/whatsapp/widget-bus';

/**
 * Full-page booking screen for one converted lead. Header with the trip and
 * who is on it, then one tab per booking kind (flights, hotels, ...) listing
 * every item with the facts needed to book it and the hold / fund / issue
 * actions, plus the customer / travellers overview and the case documents.
 */

const fmtDate = (d: Date | null | undefined): string =>
  d ? d.toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric' }) : '-';
const fmtMoney = (n: number, currency: string): string =>
  `${currency} ${new Intl.NumberFormat('en-IN', { maximumFractionDigits: 0 }).format(n)}`;

const STATUS_LABEL: Record<OpsBookingSheet['case']['status'], string> = {
  QUEUED: 'Unassigned',
  ASSIGNED: 'Fresh',
  IN_PROGRESS: 'In progress',
  COMPLETED: 'Completed',
  CANCELLED: 'Cancelled',
};
type Tone = 'success' | 'warning' | 'secondary' | 'destructive';
const DOC_LABEL: Record<string, { label: string; tone: Tone }> = {
  PENDING: { label: 'Docs not uploaded', tone: 'warning' },
  PARTIAL: { label: 'Docs partial', tone: 'warning' },
  SUBMITTED: { label: 'Docs submitted', tone: 'secondary' },
  VERIFIED: { label: 'Docs verified', tone: 'success' },
  CANCELLED: { label: 'Docs cancelled', tone: 'secondary' },
};
const CONTRACT_LABEL: Record<ContractStatus, { label: string; tone: Tone }> = {
  DRAFT: { label: 'Contract not sent', tone: 'warning' },
  SENT: { label: 'Contract not signed', tone: 'warning' },
  VIEWED: { label: 'Contract not signed', tone: 'warning' },
  CUSTOMER_SIGNED: { label: 'Customer signed', tone: 'secondary' },
  COMPLETED: { label: 'Contract signed', tone: 'success' },
  DECLINED: { label: 'Contract declined', tone: 'destructive' },
  VOIDED: { label: 'Contract voided', tone: 'destructive' },
};
/** The customer still has to sign: a reminder goes out on WhatsApp + email. */
const CONTRACT_AWAITING_CUSTOMER = new Set<ContractStatus>(['SENT', 'VIEWED']);
/** The customer still has to upload: the upload link goes out again. */
const DOCS_AWAITING_CUSTOMER = new Set(['PENDING', 'PARTIAL']);

const paxLine = (t: OpsBookingSheet['trip']): string => {
  const parts = [`${t.adults} adult${t.adults === 1 ? '' : 's'}`];
  if (t.children)
    parts.push(
      `${t.children} child${t.children === 1 ? '' : 'ren'}${t.childAges.length ? ` (${t.childAges.join(', ')} yrs)` : ''}`,
    );
  if (t.infants) parts.push(`${t.infants} infant${t.infants === 1 ? '' : 's'}`);
  return parts.join(' + ');
};

const Fact = ({
  label,
  children,
}: {
  label: string;
  children: React.ReactNode;
}): React.JSX.Element => (
  <div className="min-w-0">
    <div className="text-[10px] font-semibold tracking-wide text-zinc-400 uppercase">{label}</div>
    <div className="text-sm text-zinc-800">{children ?? '-'}</div>
  </div>
);

/**
 * One uploaded scan: click opens it in a new tab (a short-lived signed URL
 * fetched on click, so nothing sensitive sits in the page).
 */
const UploadedFile = ({
  fileId,
  name,
  fileName,
  onOpen,
  onDownload,
  busy,
}: {
  fileId: string | null;
  name: string;
  fileName: string | null;
  onOpen: (fileId: string) => void;
  onDownload: (fileId: string) => void;
  busy: boolean;
}): React.JSX.Element => (
  <li className="group flex items-center gap-2 rounded-md px-1 py-0.5 hover:bg-zinc-50">
    <button
      type="button"
      disabled={!fileId || busy}
      onClick={() => fileId && onOpen(fileId)}
      title={fileId ? 'View the uploaded document' : undefined}
      className="flex min-w-0 flex-1 items-center gap-2 text-left disabled:cursor-default"
    >
      <FileCheck2 className="h-3.5 w-3.5 shrink-0 text-emerald-600" />
      <span className="font-medium text-zinc-900">{name}</span>
      {fileName && <span className="truncate text-xs text-zinc-500">{fileName}</span>}
    </button>
    {fileId && (
      <span className="flex shrink-0 items-center gap-1">
        <button
          type="button"
          disabled={busy}
          onClick={() => onOpen(fileId)}
          title="View"
          aria-label="View document"
          className="rounded p-1 text-zinc-300 group-hover:text-zinc-500 hover:bg-zinc-100 hover:text-zinc-700"
        >
          <Eye className="h-3.5 w-3.5" />
        </button>
        <button
          type="button"
          disabled={busy}
          onClick={() => onDownload(fileId)}
          title="Download"
          aria-label="Download document"
          className="rounded p-1 text-zinc-300 group-hover:text-zinc-500 hover:bg-zinc-100 hover:text-zinc-700"
        >
          <Download className="h-3.5 w-3.5" />
        </button>
      </span>
    )}
  </li>
);

const DOC_ACCEPT = 'application/pdf,image/jpeg,image/png,image/webp,.pdf,.jpg,.jpeg,.png,.webp';
/** The customer (or ops, for them) may still add files in these states. */
const DOC_OPEN_STATUSES = new Set(['PENDING', 'PARTIAL', 'SUBMITTED']);

/**
 * Ops uploading a passport / PAN on the customer's behalf (a scan that came
 * on WhatsApp, say). Same endpoint rules as the customer's own upload.
 */
const TravellerDocumentUploadForm = ({
  requestId,
  leadId,
  caseId,
  international,
  onDone,
}: {
  requestId: string;
  leadId: string;
  caseId: string;
  international: boolean;
  onDone: () => void;
}): React.JSX.Element => {
  const qc = useQueryClient();
  const upload = useUploadDocumentFile(leadId);
  const fileRef = useRef<HTMLInputElement>(null);
  const [file, setFile] = useState<File | null>(null);
  const [type, setType] = useState<TravellerDocumentType>('PAN');
  const [travellerName, setTravellerName] = useState('');
  const [error, setError] = useState<string | null>(null);

  const submit = async (): Promise<void> => {
    if (!file) return setError('Choose a file');
    if (file.size > TRAVELLER_DOCUMENT_MAX_BYTES) return setError('File is too large (max 8MB)');
    const contentBase64 = await fileToBase64(file);
    upload.mutate(
      {
        id: requestId,
        input: {
          type,
          fileName: file.name,
          contentBase64,
          ...(travellerName.trim() ? { travellerName: travellerName.trim() } : {}),
        },
      },
      {
        onSuccess: () => {
          void qc.invalidateQueries({ queryKey: opsCasesQK.sheet(caseId) });
          onDone();
        },
      },
    );
  };

  return (
    <div className="mt-3 rounded-lg border border-zinc-200 bg-zinc-50 p-3">
      <button
        type="button"
        onClick={() => fileRef.current?.click()}
        className={cn(
          'flex w-full items-center gap-3 rounded-lg border-2 border-dashed bg-white px-3 py-3 text-left text-sm',
          file ? 'border-emerald-300 bg-emerald-50' : 'border-zinc-300 hover:bg-zinc-50',
        )}
      >
        <Upload className="h-4 w-4 text-zinc-500" />
        <span className="min-w-0 flex-1 truncate">
          {file ? file.name : 'Choose a PDF, JPG, PNG or WebP (max 8MB)'}
        </span>
      </button>
      <input
        ref={fileRef}
        type="file"
        accept={DOC_ACCEPT}
        className="sr-only"
        onChange={(e) => {
          setFile(e.target.files?.[0] ?? null);
          setError(null);
          e.target.value = '';
        }}
      />
      <div className="mt-2 grid gap-2 sm:grid-cols-2">
        <UzSelect<TravellerDocumentType>
          label="Document"
          value={type}
          onValueChange={setType}
          options={[
            { value: 'PAN', label: 'PAN' },
            ...(international ? [{ value: 'PASSPORT' as const, label: 'Passport' }] : []),
          ]}
        />
        <UzInput
          label="Traveller name (optional)"
          hint="As printed on the document"
          value={travellerName}
          onChange={(e) => setTravellerName(e.target.value)}
        />
      </div>
      {error ? <p className="mt-1 text-xs text-red-600">{error}</p> : null}
      <div className="mt-3 flex justify-end gap-2">
        <UzButton size="sm" variant="ghost" onClick={onDone} disabled={upload.isPending}>
          Cancel
        </UzButton>
        <UzButton size="sm" loading={upload.isPending} onClick={() => void submit()}>
          <Upload className="mr-1.5 h-3.5 w-3.5" /> Upload
        </UzButton>
      </div>
    </div>
  );
};

const Overview = ({
  sheet,
  canWork,
}: {
  sheet: OpsBookingSheet;
  canWork: boolean;
}): React.JSX.Element => {
  const { trip, customer, documents } = sheet;
  // Package-included rows (quoted 0) are not bookings to track.
  const items = sheet.items.filter((i) => !i.bundled);
  const qc = useQueryClient();
  const openFile = useOpenDocumentFile();
  const downloadFile = useDownloadDocumentFile();
  const verify = useVerifyDocumentRequest(sheet.case.leadId);
  const [uploading, setUploading] = useState(false);
  const fileBusy = openFile.isPending || downloadFile.isPending;
  // Ops may verify a submitted set and add files while the request is open.
  const docRequestId = documents.requestId;
  const canVerify = canWork && docRequestId !== null && documents.status === 'SUBMITTED';
  const canUpload =
    canWork && docRequestId !== null && DOC_OPEN_STATUSES.has(documents.status ?? '');
  const passports = documents.travellers.filter((t) => t.type === 'PASSPORT' && !t.rejected);
  const pans = documents.travellers.filter((t) => t.type === 'PAN' && !t.rejected);
  const pending = items.filter((i) => i.status !== 'ISSUED');
  const held = items.filter((i) => i.status === 'HOLD');
  return (
    <div className="grid gap-4 lg:grid-cols-3">
      <UzCard title="Trip" className="lg:col-span-2">
        <div className="grid gap-x-6 gap-y-3 sm:grid-cols-3">
          <Fact label="Itinerary">{trip.title}</Fact>
          <Fact label="Dates">
            {trip.travelStartDate
              ? `${fmtDate(trip.travelStartDate)} - ${fmtDate(trip.travelEndDate)}`
              : '-'}
          </Fact>
          <Fact label="Duration">
            {trip.numberOfNights || trip.numberOfDays
              ? `${trip.numberOfNights}N / ${trip.numberOfDays}D`
              : '-'}
          </Fact>
          <Fact label="Travellers">{paxLine(trip)}</Fact>
          <Fact label="Rooms">{trip.roomsCount || '-'}</Fact>
          <Fact label="Hotel category">{trip.hotelCategory ?? '-'}</Fact>
          <Fact label="Departs from">{trip.fromCity ?? '-'}</Fact>
          <Fact label="Destination">{sheet.case.destinationName ?? '-'}</Fact>
          <Fact label="Sold for">
            {sheet.case.saleAmount !== null
              ? fmtMoney(sheet.case.saleAmount, 'INR')
              : fmtMoney(trip.grandTotal, trip.currency)}
          </Fact>
          {/* The customer's money vs what is going to suppliers (every desk's case). */}
          {sheet.money ? (
            <>
              <Fact label="Received from customer">
                {fmtMoney(sheet.money.receivedInr, 'INR')}
                {sheet.money.balanceInr !== null && sheet.money.balanceInr > 0 ? (
                  <span className="ml-1 text-xs text-amber-700">
                    · owes {fmtMoney(sheet.money.balanceInr, 'INR')}
                  </span>
                ) : sheet.money.balanceInr !== null ? (
                  <span className="ml-1 text-xs text-emerald-700">· paid in full</span>
                ) : null}
              </Fact>
              <Fact label="Paid to suppliers">
                {fmtMoney(sheet.money.utilisedInr, 'INR')}
                <span className="ml-1 text-xs text-zinc-500">
                  · asked {fmtMoney(sheet.money.committedInr, 'INR')}
                </span>
              </Fact>
              <Fact label="In hand">
                <span
                  className={cn(sheet.money.availableInr < 0 ? 'text-red-700' : 'text-emerald-700')}
                >
                  {sheet.money.availableInr < 0
                    ? `short by ${fmtMoney(Math.abs(sheet.money.availableInr), 'INR')}`
                    : fmtMoney(sheet.money.availableInr, 'INR')}
                </span>
              </Fact>
            </>
          ) : null}
        </div>
      </UzCard>

      <UzCard title="Customer">
        <div className="space-y-2 text-sm">
          <div className="font-semibold text-zinc-900">{customer.name}</div>
          {customer.mobile ? (
            <a
              href={`tel:${customer.mobile}`}
              className="flex items-center gap-2 text-zinc-700 hover:underline"
            >
              <Phone className="h-3.5 w-3.5 text-zinc-400" /> {customer.mobile}
            </a>
          ) : null}
          {customer.email ? (
            <a
              href={`mailto:${customer.email}`}
              className="flex items-center gap-2 text-zinc-700 hover:underline"
            >
              <Mail className="h-3.5 w-3.5 text-zinc-400" /> {customer.email}
            </a>
          ) : null}
          <div className="text-xs text-zinc-500">
            Sold by {sheet.case.salesOwnerName ?? '-'} · {sheet.case.leadReference}
          </div>
        </div>
      </UzCard>

      <UzCard
        title="Travellers (as uploaded)"
        actions={
          <span className="flex flex-wrap items-center gap-2">
            {documents.status ? (
              <UzBadge tone={DOC_LABEL[documents.status]?.tone ?? 'secondary'}>
                {DOC_LABEL[documents.status]?.label ?? documents.status}
              </UzBadge>
            ) : null}
            {canUpload && !uploading ? (
              <UzButton size="sm" variant="outline" onClick={() => setUploading(true)}>
                <Upload className="mr-1.5 h-3.5 w-3.5" /> Upload for customer
              </UzButton>
            ) : null}
            {canVerify && docRequestId ? (
              <UzButton
                size="sm"
                loading={verify.isPending}
                onClick={() =>
                  verify.mutate(docRequestId, {
                    onSuccess: () =>
                      void qc.invalidateQueries({ queryKey: opsCasesQK.sheet(sheet.case.id) }),
                  })
                }
                title="Every required document is in and looks right"
              >
                <BadgeCheck className="mr-1.5 h-3.5 w-3.5" /> Mark verified
              </UzButton>
            ) : null}
          </span>
        }
        className="lg:col-span-2"
      >
        {documents.travellers.length === 0 ? (
          <p className="text-sm text-zinc-500">
            Nothing uploaded yet. Names on tickets must match the passport / PAN exactly, so wait
            for the documents (Documents tab on the lead) before issuing international items.
          </p>
        ) : (
          <div className="grid gap-4 sm:grid-cols-2">
            <div>
              <div className="mb-1 flex items-center gap-1.5 text-[11px] font-semibold tracking-wide text-zinc-500 uppercase">
                <Users className="h-3 w-3" /> Passports · {passports.length}
                {sheet.case.tripScope === 'INTERNATIONAL'
                  ? ` of ${trip.adults + trip.children + trip.infants}`
                  : ''}
              </div>
              {passports.length ? (
                <ul className="space-y-0.5 text-sm">
                  {passports.map((p, i) => (
                    <UploadedFile
                      key={p.fileId ?? i}
                      fileId={p.fileId}
                      name={p.travellerName ?? 'Name not given'}
                      fileName={p.fileName}
                      onOpen={(id) => openFile.mutate(id)}
                      onDownload={(id) => downloadFile.mutate(id)}
                      busy={fileBusy}
                    />
                  ))}
                </ul>
              ) : (
                <p className="text-sm text-zinc-500">
                  {sheet.case.tripScope === 'INTERNATIONAL'
                    ? 'None yet.'
                    : 'Not needed (domestic).'}
                </p>
              )}
            </div>
            <div>
              <div className="mb-1 text-[11px] font-semibold tracking-wide text-zinc-500 uppercase">
                PAN · {pans.length}
              </div>
              {pans.length ? (
                <ul className="space-y-0.5 text-sm">
                  {pans.map((p, i) => (
                    <UploadedFile
                      key={p.fileId ?? i}
                      fileId={p.fileId}
                      name={p.travellerName ?? p.fileName}
                      fileName={p.travellerName ? p.fileName : null}
                      onOpen={(id) => openFile.mutate(id)}
                      onDownload={(id) => downloadFile.mutate(id)}
                      busy={fileBusy}
                    />
                  ))}
                </ul>
              ) : (
                <p className="text-sm text-zinc-500">None yet.</p>
              )}
            </div>
          </div>
        )}
        {uploading && docRequestId ? (
          <TravellerDocumentUploadForm
            requestId={docRequestId}
            leadId={sheet.case.leadId}
            caseId={sheet.case.id}
            international={sheet.case.tripScope === 'INTERNATIONAL'}
            onDone={() => setUploading(false)}
          />
        ) : null}
      </UzCard>

      <UzCard title="Booking progress">
        {/* Issued / on hold / total per kind, with a totals row. */}
        <table className="w-full text-sm">
          <thead>
            <tr className="text-[10px] font-semibold tracking-wide text-zinc-400 uppercase">
              <th className="pb-1.5 text-left font-semibold">Item</th>
              <th className="pb-1.5 text-right font-semibold">Issued</th>
              <th className="pb-1.5 text-right font-semibold">Hold</th>
              <th className="pb-1.5 text-right font-semibold">Total</th>
            </tr>
          </thead>
          <tbody>
            {KIND_ORDER.map((k) => {
              const ofKind = items.filter((i) => actionableKindOf(i.kind, i.vasKind) === k);
              if (!ofKind.length) return null;
              const done = ofKind.filter((i) => i.status === 'ISSUED').length;
              const onHold = ofKind.filter((i) => i.status === 'HOLD').length;
              const Icon = KIND_META[k].icon;
              return (
                <tr key={k}>
                  <td className="py-1">
                    <span className="inline-flex items-center gap-1.5 text-zinc-700">
                      <Icon className="h-3.5 w-3.5 text-zinc-400" /> {KIND_META[k].plural}
                    </span>
                  </td>
                  <td
                    className={cn(
                      'py-1 text-right tabular-nums',
                      done === ofKind.length ? 'font-semibold text-emerald-700' : 'text-zinc-900',
                    )}
                  >
                    {done}
                  </td>
                  <td
                    className={cn(
                      'py-1 text-right tabular-nums',
                      onHold > 0 ? 'font-semibold text-amber-700' : 'text-zinc-400',
                    )}
                  >
                    {onHold}
                  </td>
                  <td className="py-1 text-right text-zinc-900 tabular-nums">{ofKind.length}</td>
                </tr>
              );
            })}
            <tr className="border-t border-zinc-200 font-semibold">
              <td className="pt-1.5 text-zinc-900">Total</td>
              <td
                className={cn(
                  'pt-1.5 text-right tabular-nums',
                  items.length > 0 && pending.length === 0 ? 'text-emerald-700' : 'text-zinc-900',
                )}
              >
                {items.length - pending.length}
              </td>
              <td
                className={cn(
                  'pt-1.5 text-right tabular-nums',
                  held.length > 0 ? 'text-amber-700' : 'text-zinc-400',
                )}
              >
                {held.length}
              </td>
              <td className="pt-1.5 text-right text-zinc-900 tabular-nums">{items.length}</td>
            </tr>
          </tbody>
        </table>
        <div className="mt-3 border-t border-zinc-100 pt-2 text-xs text-zinc-500">
          {pending.length} to finish · {held.length} on hold
          {sheet.case.nextPaymentDue ? (
            <>
              {' '}
              · next pay-by{' '}
              <span className={payByTone(sheet.case.nextPaymentDue).className}>
                {fmtDate(sheet.case.nextPaymentDue)}
              </span>
            </>
          ) : null}
        </div>
      </UzCard>
    </div>
  );
};

export const OpsCasePage = (): React.JSX.Element => {
  const { id = '' } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const [overviewOpen, setOverviewOpen] = useState(true);
  const me = useCurrentUser();
  const sheet = useOpsBookingSheet(id || null);
  const open = useOpenOpsCase();
  const complete = useCompleteOpsCase();
  const reopen = useReopenOpsCase();

  const data = sheet.data;
  const leadId = data?.case.leadId ?? '';
  const remindContract = useResendContract(leadId);
  const remindDocs = useResendDocumentRequest(leadId);
  const requestDocs = useCreateDocumentRequest(leadId);
  const qc = useQueryClient();
  // What the customer still owes; each badge gets its own re-send button.
  const contractPendingId =
    data?.contract && CONTRACT_AWAITING_CUSTOMER.has(data.contract.status)
      ? data.contract.id
      : null;
  const docsPendingId =
    data && DOCS_AWAITING_CUSTOMER.has(data.documents.status ?? '')
      ? data.documents.requestId
      : null;
  const isManager = me?.role === 'ADMIN' || me?.role === 'OPERATIONS_LEAD';
  // Managers and the lead's Operations owner book on any open case, whether
  // or not the desk has been dealt; a desk executive only once it is theirs.
  const isOwner = Boolean(data && data.case.coordinator?.id === me?.id);
  const canWork = Boolean(
    data &&
    data.case.status !== 'CANCELLED' &&
    (isManager ||
      isOwner ||
      (data.case.assignedTo?.id === me?.id && data.case.status !== 'QUEUED')),
  );

  if (sheet.isLoading) {
    return (
      <PageShell title="Bookings">
        <div className="flex items-center gap-2 py-10 text-sm text-zinc-500">
          <UzSpinner size="sm" /> Loading the booking sheet…
        </div>
      </PageShell>
    );
  }
  if (!data) {
    return (
      <PageShell title="Bookings">
        <UzCard>
          <p className="py-6 text-sm text-zinc-600">
            This converted lead could not be loaded. It may be assigned to someone else.
          </p>
          <UzButton variant="outline" onClick={() => navigate('/operations/converted-leads')}>
            <ArrowLeft className="mr-1.5 h-3.5 w-3.5" /> Back to Converted leads
          </UzButton>
        </UzCard>
      </PageShell>
    );
  }

  const row = data.case;
  return (
    <PageShell
      title={
        <span className="flex flex-wrap items-center gap-2">
          {row.customerName}
          <DepartmentChip department={row.department} className="text-xs" />
          <UzBadge
            tone={
              row.status === 'COMPLETED'
                ? 'success'
                : row.status === 'CANCELLED'
                  ? 'destructive'
                  : 'secondary'
            }
          >
            {STATUS_LABEL[row.status]}
          </UzBadge>
          <UzBadge tone={data.contract ? CONTRACT_LABEL[data.contract.status].tone : 'warning'}>
            {data.contract ? CONTRACT_LABEL[data.contract.status].label : 'No contract'}
          </UzBadge>
          {contractPendingId ? (
            <UzButton
              size="sm"
              variant="outline"
              loading={remindContract.isPending}
              onClick={() => remindContract.mutate(contractPendingId)}
              title="Re-send the contract signing link on WhatsApp + email"
            >
              <BellRing className="mr-1.5 h-3.5 w-3.5" /> Remind to sign
            </UzButton>
          ) : null}
          <UzBadge
            tone={
              row.documentStatus ? (DOC_LABEL[row.documentStatus]?.tone ?? 'secondary') : 'warning'
            }
          >
            {row.documentStatus
              ? (DOC_LABEL[row.documentStatus]?.label ?? row.documentStatus)
              : 'Docs not requested'}
          </UzBadge>
          {docsPendingId ? (
            <UzButton
              size="sm"
              variant="outline"
              loading={remindDocs.isPending}
              onClick={() => remindDocs.mutate(docsPendingId)}
              title="Re-send the document upload link on WhatsApp + email"
            >
              <BellRing className="mr-1.5 h-3.5 w-3.5" /> Remind to upload
            </UzButton>
          ) : null}
          {/* No request on the lead at all (the automatic send on the contract
              failed, or the deal closed without one): raise it from here. */}
          {!row.documentStatus && canWork ? (
            <UzButton
              size="sm"
              variant="outline"
              loading={requestDocs.isPending}
              onClick={() =>
                requestDocs.mutate(
                  {},
                  {
                    // Also on a 409: the API links the lead's existing request to the case.
                    onSettled: () =>
                      void qc.invalidateQueries({ queryKey: opsCasesQK.sheet(row.id) }),
                  },
                )
              }
              title="Send the PAN / passport upload link on WhatsApp + email"
            >
              <FilePlus2 className="mr-1.5 h-3.5 w-3.5" /> Request documents
            </UzButton>
          ) : null}
        </span>
      }
      description={[
        row.leadReference,
        data.trip.title,
        data.trip.travelStartDate ? fmtDate(data.trip.travelStartDate) : null,
        paxLine(data.trip),
        row.coordinator ? `owner: ${row.coordinator.fullName}` : 'no owner',
        row.assignedTo
          ? `${DEPARTMENT_META[row.department].label}: ${row.assignedTo.fullName}`
          : `${DEPARTMENT_META[row.department].label}: not dealt`,
      ]
        .filter(Boolean)
        .join(' · ')}
      actions={
        <div className="flex flex-wrap gap-2">
          <UzButton variant="ghost" onClick={() => navigate('/operations/converted-leads')}>
            <ArrowLeft className="mr-1.5 h-3.5 w-3.5" /> Converted leads
          </UzButton>
          <UzButton variant="outline" onClick={() => navigate(`/leads/${row.leadId}`)}>
            <ExternalLink className="mr-1.5 h-3.5 w-3.5" /> Open lead
          </UzButton>
          {/* Only the itinerary the lead was won on, never a working draft:
              the customer's published share page when it exists, else the
              CRM itinerary page. */}
          {row.wonItineraryId ? (
            <UzButton
              variant="outline"
              onClick={() =>
                window.open(
                  row.wonItineraryShareUrl ?? `/itineraries/${row.wonItineraryId}`,
                  '_blank',
                  'noopener,noreferrer',
                )
              }
              title={
                row.wonItineraryShareUrl
                  ? 'Opens the published itinerary page in a new tab'
                  : 'Opens the won itinerary in a new tab'
              }
            >
              <Map className="mr-1.5 h-3.5 w-3.5" /> View itinerary
            </UzButton>
          ) : null}
          {row.whatsappGroup ? (
            <UzButton
              variant="outline"
              onClick={() => openWhatsAppConversation(row.whatsappGroup!.conversationId)}
              title="The lead's WhatsApp group: customer + operations team"
            >
              <FaWhatsapp className="mr-1.5 h-3.5 w-3.5 text-[#25D366]" /> WhatsApp group
            </UzButton>
          ) : null}
          {row.status === 'ASSIGNED' && canWork ? (
            <UzButton loading={open.isPending} onClick={() => open.mutate(row.id)}>
              Start bookings
            </UzButton>
          ) : null}
          {row.status === 'IN_PROGRESS' && canWork ? (
            <UzButton
              variant="outline"
              loading={complete.isPending}
              onClick={() => complete.mutate(row.id)}
              title="Every item is issued"
            >
              <CheckCircle2 className="mr-1.5 h-3.5 w-3.5" /> All bookings done
            </UzButton>
          ) : null}
          {row.status === 'COMPLETED' && canWork ? (
            <UzButton
              variant="ghost"
              loading={reopen.isPending}
              onClick={() => reopen.mutate(row.id)}
            >
              <RotateCcw className="mr-1.5 h-3.5 w-3.5" /> Reopen
            </UzButton>
          ) : null}
        </div>
      }
    >
      {row.status === 'QUEUED' ? (
        <p className="mb-3 rounded-lg bg-amber-50 px-3 py-2 text-sm text-amber-800">
          {row.coordinator
            ? `No ${DEPARTMENT_META[row.department].label} executive on this desk yet; ${row.coordinator.fullName} owns the lead.`
            : 'This lead has no Operations owner yet.'}{' '}
          {isManager || isOwner
            ? 'You can book it now, or deal the desk from the Converted leads screen.'
            : 'Assign it from the Converted leads screen before booking.'}
        </p>
      ) : null}
      <div className="space-y-4">
        <div className="flex flex-wrap items-center justify-between gap-2">
          <div className="text-[11px] font-semibold tracking-[0.06em] text-zinc-500 uppercase">
            Bookings
          </div>
          <BookingJumpNav items={data.items} />
        </div>

        <section className="overflow-hidden rounded-xl border border-zinc-200 bg-white">
          <button
            type="button"
            onClick={() => setOverviewOpen((v) => !v)}
            className="flex w-full items-center justify-between gap-3 px-4 py-3 text-left"
          >
            <span className="text-base font-semibold tracking-tight text-zinc-900">
              Trip, customer &amp; travellers
              <span className="ml-2 text-sm font-normal text-zinc-500">
                {paxLine(data.trip)}
                {data.trip.travelStartDate ? ` · ${fmtDate(data.trip.travelStartDate)}` : ''}
                {data.documents.status
                  ? ` · ${DOC_LABEL[data.documents.status]?.label ?? data.documents.status}`
                  : ''}
              </span>
            </span>
            <ChevronDown
              className={cn(
                'h-4 w-4 text-zinc-400 transition-transform',
                overviewOpen && 'rotate-180',
              )}
            />
          </button>
          {overviewOpen ? (
            <div className="border-t border-zinc-100 bg-zinc-50/40 p-4">
              <Overview sheet={data} canWork={canWork} />
            </div>
          ) : null}
        </section>

        <OpsBookingSections items={data.items} canWork={canWork} />

        <CaseDocuments caseId={row.id} canWork={canWork} items={data.items} />
      </div>
    </PageShell>
  );
};
